/**
 * Composed-deck Zustand selectors.
 *
 * React hooks that read a card or deck honouring its split-canonical
 * halves and re-render only when the relevant slice changes.
 *
 * Design constraints:
 *   - Personal-only deck: read directly from
 *     `useFlashkaStore.cards[deckId]` / `useFlashkaStore.deckProperties[deckId]`.
 *   - Linked deck: shared half comes from
 *     `useStudyGroupStore.groups[gid].cards[deckId]` /
 *     `.deckProps[deckId]`; personal half comes from the local
 *     `useFlashkaStore.cards[deckId]` row (which carries the
 *     personal-scope fields stamped at write time).
 *
 * The previous `linkedCardOverlay` / `linkedDeckOverlay` entities were
 * inert placeholders — no writer ever populated them, and composeCard
 * always fell through to the legacy `useFlashkaStore.cards` fallback.
 * They have been removed; this file now reads the personal half from
 * the canonical local cards/deckProperties slices directly.
 */

import type {
  DeckId,
  SyncCardProps,
  SyncDeckMeta,
} from '@flashka/shared/sync-engine/schema/shared-types';

import { useMemo } from 'react';

import {
  getActiveLinkedGroupNow,
  useActiveLinkedGroup,
} from '../../study-groups/active-links';
import { composeCardCached, composeCardsMap, composeDeckMeta } from './composed-deck';
import { useFlashkaStore } from './flashka-store';
import { useStudyGroupStore } from './study-group-store';

// ---------------------------------------------------------------------------
// Link discovery
// ---------------------------------------------------------------------------

/**
 * The link a deck's composed reads come from: its first link to a group the
 * viewer is STILL in (`study-groups/active-links`), or null for a personal
 * deck and for one whose every group the viewer has left.
 *
 * A deck may be linked into several groups at once without forking — every
 * link owner-only (see `sharing-rules.ts`) — and the owner's writes reach
 * every one of those rows alike (`scoped-deck/properties.ts`, and the server
 * for cards), so a read without a group in hand may take any live one. What
 * it must never take is a dormant link: that group's row is not being kept
 * up to date for this viewer, and reading it shows a stale or empty deck.
 */
function useActiveLinkFor(deckId: string) {
  const links = useFlashkaStore((s) => s.deckProperties[deckId]?.linkedGroups);
  return useActiveLinkedGroup(links);
}

/**
 * Resolve the canonical groupId for a deck: undefined for a personal-only
 * deck (or one whose every link is dormant), else its first LIVE link's.
 */
export function useLinkedGroupIdFor(deckId: string): string | undefined {
  return useActiveLinkFor(deckId)?.groupId;
}

/** Snapshot variant — for use outside React render. */
export function getLinkedGroupIdFor(deckId: string): string | undefined {
  return getActiveLinkedGroupNow(
    useFlashkaStore.getState().deckProperties[deckId]?.linkedGroups,
  )?.groupId;
}

/**
 * Resolve the SG-side deckId for a personal deck.
 *
 * The SG store keys deck rows + card buckets by the SG-side `deckId`
 * (allocated freshly by `sgGroup(...).decks.add(...)`), NOT by the
 * personal deckId — so the composed selectors below must dereference
 * the live link's `sgDeckId` before looking up in `useStudyGroupStore`.
 * Using the personal `deckId` here was a latent bug that hid every
 * shared field from the owner's composed view after sharing.
 */
export function useSgDeckIdFor(deckId: string): string | undefined {
  return useActiveLinkFor(deckId)?.sgDeckId;
}

// ---------------------------------------------------------------------------
// Card-level composed selectors
// ---------------------------------------------------------------------------

/**
 * Reactive single-card read. Re-renders only when this card's shared
 * half OR its personal-scope row changes.
 */
export function useComposedCard(
  deckId: DeckId,
  cardId: string,
): SyncCardProps | undefined {
  const groupId = useLinkedGroupIdFor(deckId);
  const sgDeckId = useSgDeckIdFor(deckId);
  const personalCard = useFlashkaStore((s) => s.cards[deckId]?.[cardId]);
  const sharedCard = useStudyGroupStore((s) =>
    groupId && sgDeckId ? s.groups[groupId]?.cards[sgDeckId]?.[cardId] : undefined,
  );
  if (!groupId) return personalCard;
  // `personalCard` is the local `useFlashkaStore.cards` row — personal-
  // scope fields (starred, studyState, notes, tags, chat) are stamped
  // here at write time. The compositor merges them onto the SG-owned
  // shared half. Cached on the two input objects, so a re-render with the
  // same halves returns the same composed card.
  return composeCardCached(sharedCard, personalCard);
}

/**
 * Reactive multi-card read as a sorted array.
 *
 * Internally calls `useComposedCardsRecord` and converts to an array.
 * The sort is left to the consumer — the underlying store doesn't sort.
 */
export function useComposedCards(deckId: DeckId): SyncCardProps[] {
  const map = useComposedCardsRecord(deckId);
  return useMemo(() => Object.values(map), [map]);
}

/**
 * Reactive multi-card read as a record. Memoised on the inputs so
 * downstream consumers can rely on referential equality.
 */
export function useComposedCardsRecord(deckId: DeckId): Record<string, SyncCardProps> {
  const groupId = useLinkedGroupIdFor(deckId);
  const sgDeckId = useSgDeckIdFor(deckId);
  const personalMap = useFlashkaStore((s) => s.cards[deckId]);
  const sharedMap = useStudyGroupStore((s) =>
    groupId && sgDeckId ? s.groups[groupId]?.cards[sgDeckId] : undefined,
  );
  return useMemo(() => {
    if (!groupId) return personalMap ?? {};
    return composeCardsMap(sharedMap, personalMap);
  }, [groupId, personalMap, sharedMap]);
}

// ---------------------------------------------------------------------------
// Deck-meta composed selector
// ---------------------------------------------------------------------------

/**
 * Reactive composed deck-meta. For personal-only decks returns the
 * `deckProperties[deckId]` row directly. For linked decks merges the
 * SG-side `deckProps[deckId]` with the local personal-scope fields.
 */
export function useComposedDeckMeta(deckId: DeckId): SyncDeckMeta | undefined {
  const personal = useFlashkaStore((s) => s.deckProperties[deckId]);
  // SG store keys deck rows by `sgDeckId` (allocated freshly in
  // `sgGroup(...).decks.add(...)`), not by the personal `deckId`. Read
  // the live link's `sgDeckId` off the personal row we already selected.
  const link = useActiveLinkedGroup(personal?.linkedGroups);
  const groupId = link?.groupId;
  const sgDeckId = link?.sgDeckId;
  const sharedSGProps = useStudyGroupStore((s) =>
    groupId && sgDeckId ? s.groups[groupId]?.deckProps[sgDeckId] : undefined,
  );
  if (!groupId) return personal;
  // SGDeckProps is structurally a subset of SyncDeckMeta — cast through
  // unknown to avoid spurious overlap errors.
  return composeDeckMeta(
    sharedSGProps as unknown as Partial<SyncDeckMeta> | undefined,
    personal,
  );
}

/**
 * Snapshot variant of {@link useComposedDeckMeta} — for writers that read the
 * deck outside React render (attach / rename / remove a document, add a note).
 *
 * One deliberate difference: a LINKED deck whose group row has not arrived
 * yet (offline start, group not opened) falls back to the personal row rather
 * than reading as missing. A writer that saw `undefined` would refuse the
 * edit outright, where the hook's caller only shows a loading state.
 */
export function getComposedDeckMeta(deckId: DeckId): SyncDeckMeta | undefined {
  const personal = useFlashkaStore.getState().deckProperties[deckId];
  const link = getActiveLinkedGroupNow(personal?.linkedGroups);
  if (!link) return personal;
  const sharedSGProps =
    useStudyGroupStore.getState().groups[link.groupId]?.deckProps[link.sgDeckId];
  if (!sharedSGProps) return personal;
  return composeDeckMeta(
    sharedSGProps as unknown as Partial<SyncDeckMeta> | undefined,
    personal,
  );
}
